import React, { useState, useMemo, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  Button,
  Badge,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionBody,
} from "reactstrap";
import Select from "react-select";
import { phoneNumberFormatter } from "../../slices/phone-number-formatter";
import TableContainer from "../../Components/Common/TableContainer";
import Loader from "../../Components/Common/Loader";
import { getSellersQuery, getSellerTransactionsQuery } from "../../queries";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";
import Flatpickr from "react-flatpickr";
import { getFlatpickrLocale } from "../../utils/flatpickr";
import { retryPayment } from "../../api/seller";
import { useTranslation } from "react-i18next";

const Transactions = () => {
  const [status, setStatus] = useState();
  const [seller, setSeller] = useState();
  const [sellerOptions, setSellersOptions] = useState([]);
  const [date, setDate] = useState();
  const [modal, setModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [open, setOpen] = useState("1");
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();

  const fpRef = React.useRef(null);

  useEffect(() => {
    if (fpRef.current && fpRef.current.flatpickr && fpRef.current.flatpickr.altInput) {
      fpRef.current.flatpickr.altInput.placeholder = t("Select a date");
    }
  }, [t]);

  const flatpickrLocale = getFlatpickrLocale(i18n.language);

  const [minPriceInput, setMinPriceInput] = useState("");
  const [maxPriceInput, setMaxPriceInput] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => setMinPrice(minPriceInput), 500);
    return () => clearTimeout(handler);
  }, [minPriceInput]);

  useEffect(() => {
    const handler = setTimeout(() => setMaxPrice(maxPriceInput), 500);
    return () => clearTimeout(handler);
  }, [maxPriceInput]);

  useEffect(() => {
    const handler = setTimeout(() => setSearch(searchInput), 500);
    return () => clearTimeout(handler);
  }, [searchInput]);

  const { data: payments, isLoading } = useQuery({
    ...getSellerTransactionsQuery({
      page,
      fromCreatedAt: date?.[0],
      toCreatedAt: date?.[1],
      fromPaymentAmount: minPrice ? minPrice * 100 : undefined,
      toPaymentAmount: maxPrice ? maxPrice * 100 : undefined,
      containsPosHardwareSerialNumber: search || undefined,
      sellerId: seller?.value,
      status: status?.value,
    }),
  });

  const { data: sellers } = useQuery({
    ...getSellersQuery({
      page: 1,
      size: 1000,
    }),
  });

  const onRetry = (paymentid) => {
    retryPayment(paymentid)
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: [
            "seller-transactions",
            {
              page,
              fromCreatedAt: date?.[0],
              toCreatedAt: date?.[1],
              fromPaymentAmount: minPrice ? minPrice * 100 : undefined,
              toPaymentAmount: maxPrice ? maxPrice * 100 : undefined,
              containsPosHardwareSerialNumber: search || undefined,
            },
          ],
        });
      })
      .catch((err) => {
        if (err?.data.error?.message || err?.data.error?.code) {
          alert(err?.data.error?.message || err?.data.error?.code);
        }
      });
  };

  const columns = useMemo(
    () => [
      {
        header: "Terminal ID",
        cell: ({ row }) =>
          `${row.original.eraSaleCallbackPayment?.paymentInfoDto?.terminalId ||
          row.original.serviceName ||
          "-"
          }`,
        enableColumnFilter: false,
      },
      {
        header: t("Terminal Serial"),
        cell: ({ row }) =>
          `${row.original?.eraSaleCallbackPayment?.posHardwareSerialNumber ||
          row.original.sellerFoundBy ||
          "-"
          }`,
        enableColumnFilter: false,
      },
      {
        header: t("Seller FIO"),
        cell: ({ row }) =>
          `${row.original.seller?.user?.firstName || "-"} ${row.original.seller?.user?.lastName || ""
          }`,
        enableColumnFilter: false,
      },
      {
        header: t("Phone"),
        cell: ({ row }) =>
          phoneNumberFormatter(row.original.seller?.user?.phoneNumber),
        enableColumnFilter: false,
      },
      {
        header: t("Date"),
        cell: ({ row }) =>
          dayjs(row.original.createdAt).format("DD.MM.YYYY, HH:mm"),
      },
      {
        header: t("Amount"),
        cell: ({ row }) =>
          `${(row.original.amount / 100).toLocaleString()} uzs`,
        enableColumnFilter: false,
      },
      {
        header: t("Status"),
        cell: ({ row }) =>
          row.original.status === "completed" ? (
            <Badge color="success">{t(row.original.status)}</Badge>
          ) : row.original.status === "failed" ? (
            <Badge color="danger">{t(row.original.status)}</Badge>
          ) : (
            <Badge color="secondary">{row.original.status}</Badge>
          ),
        enableColumnFilter: false,
      },
      {
        header: t("Actions"),
        cell: ({ row }) =>
          row.original.status === "failed" && (
            <Button
              size="sm"
              color="danger"
              className="btn"
              onClick={() => onRetry(row.original.id)}
            >
              {t("Retry")}
            </Button>
          ),
        enableColumnFilter: false,
      },
    ],
    [t]
  );

  const clearFilters = () => {
    setDate(null);
    setMinPriceInput("");
    setMaxPriceInput("");
    setSearchInput("");
    setMinPrice("");
    setMaxPrice("");
    setSearch("");
    setSeller("");
    setStatus("");
  };

  useEffect(() => {
    setPage(1);
  }, [date, minPrice, maxPrice, search]);

  useEffect(() => {
    if (sellers) {
      setSellersOptions(
        sellers?.data?.data.map((seller) => ({
          value: seller.id,
          label: `${seller.user?.firstName} ${seller.user?.lastName}`,
        }))
      );
    }
  }, [sellers]);

  document.title = "Seller Transactions | Bazaar";

  const toggle = () => {
    setModal(!modal);
  };

  const toggleAccordion = (id) => {
    if (open === id) {
      setOpen();
    } else {
      setOpen(id);
    }
  };

  const handleRowClick = (row) => {
    setSelectedTransaction(row.original);
    setOpen("1");
    toggle();
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col lg={12}>
            <Card id="terminalList">
              <CardHeader className="border-0 d-flex flex-wrap justify-content-between align-items-center gap-2">
                <h5 className="mb-0 fw-bold">{t("Seller Transactions")}</h5>
                {(date?.[0] ||
                  minPrice ||
                  maxPrice ||
                  search ||
                  seller ||
                  status) && (
                    <Button
                      color="danger"
                      size="sm"
                      onClick={clearFilters}
                      className="d-flex align-items-center justify-content-center gap-1"
                    >
                      {t("Clear Filter")}
                    </Button>
                  )}
              </CardHeader>

              <div className="py-3 px-3 mb-3 border border-dashed border-end-0 border-start-0">
                <Row className="g-3">
                  <Col xs="12" sm="6" md="4" lg="2">
                    <Flatpickr
                      ref={fpRef}
                      name="orderDate"
                      className="form-control"
                      placeholder={t("Select a date")}
                      onChange={(e) => setDate(e)}
                      value={date}
                      options={{
                        enableTime: true,
                        altInput: true,
                        mode: "range",
                        altFormat: "d M, Y, G:i K",
                        dateFormat: "d M, Y, G:i K",
                        locale: flatpickrLocale,
                      }}
                    />
                  </Col>

                  <Col xs="12" sm="6" md="4" lg="2">
                    <Input
                      type="number"
                      placeholder={t("Min Price")}
                      value={minPriceInput}
                      onChange={(e) => setMinPriceInput(e.target.value)}
                    />
                  </Col>

                  <Col xs="12" sm="6" md="4" lg="2">
                    <Input
                      type="number"
                      placeholder={t("Max Price")}
                      value={maxPriceInput}
                      onChange={(e) => setMaxPriceInput(e.target.value)}
                    />
                  </Col>

                  <Col xs="12" sm="6" md="4" lg="2">
                    <Input
                      type="text"
                      placeholder={t("Serial number")}
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                    />
                  </Col>

                  <Col xs="12" sm="6" md="4" lg="2">
                    <Select
                      value={seller}
                      options={sellerOptions}
                      onChange={(selectedOption) => setSeller(selectedOption)}
                      placeholder={t("Sellers")}
                    />
                  </Col>

                  <Col xs="12" sm="6" md="4" lg="2">
                    <Select
                      value={status}
                      options={[
                        { value: "", label: t("all") },
                        { value: "created", label: t("created") },
                        { value: "queued", label: t("queued") },
                        { value: "pending", label: t("pending") },
                        { value: "inProgress", label: t("inProgress") },
                        { value: "completed", label: t("completed") },
                        { value: "failed", label: t("failed") },
                      ]}
                      onChange={(selectedOption) => setStatus(selectedOption)}
                      placeholder={t("Status")}
                    />
                  </Col>
                </Row>
              </div>

              <div className="card-body pt-0">
                {payments?.data?.data.length > 0 && (
                  <TableContainer
                    pagination={{
                      setPage,
                      page,
                      total: payments?.pagination.TotalPages,
                    }}
                    columns={columns}
                    data={payments?.data?.data || []}
                    className="custom-header-css"
                    divClass="table-responsive table-card mb-1"
                    tableClass="align-middle table-nowrap"
                    theadClass="table-light text-muted"
                    onRowClick={handleRowClick}
                  />
                )}
                {isLoading && <Loader />}
              </div>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal isOpen={modal} toggle={toggle} centered size="lg">
        <ModalHeader toggle={toggle}>{t("Transaction Details")}</ModalHeader>
        <ModalBody>
          {selectedTransaction && (
            <Accordion open={open || "1"} toggle={toggleAccordion}>
              <AccordionItem>
                <AccordionHeader targetId="1">{t("General Information")}</AccordionHeader>
                <AccordionBody accordionId="1">
                  <div className="table-responsive">
                    <table className="table table-bordered mb-0 text-black">
                      <tbody>
                        <tr>
                          <td className="fw-bold">{t("Seller FIO")}</td>
                          <td>
                            {`${selectedTransaction.seller?.user?.firstName || "-"} ${selectedTransaction.seller?.user?.lastName || ""
                              }`}
                            <br />
                            <small className="text-muted">
                              {phoneNumberFormatter(
                                selectedTransaction.seller?.user?.phoneNumber
                              )}
                            </small>
                          </td>
                        </tr>
                        <tr>
                          <td className="fw-bold">{t("Seller ID")}</td>
                          <td>{selectedTransaction.seller?.id}</td>
                        </tr>
                        <tr>
                          <td className="fw-bold">{t("User ID")}</td>
                          <td>{selectedTransaction.seller?.user?.id}</td>
                        </tr>
                        <tr>
                          <td className="fw-bold">{t("Amount")}</td>
                          <td>
                            {(selectedTransaction.amount / 100).toLocaleString()} uzs
                          </td>
                        </tr>
                        <tr>
                          <td className="fw-bold">{t("Payment Amount")}</td>
                          <td>
                            {(selectedTransaction.paymentAmount / 100).toLocaleString()}{" "}
                            uzs
                          </td>
                        </tr>
                        <tr>
                          <td className="fw-bold">{t("Status")}</td>
                          <td>
                            {selectedTransaction.status === "completed" ? (
                              <Badge color="success">
                                {t(selectedTransaction.status)}
                              </Badge>
                            ) : selectedTransaction.status === "failed" ? (
                              <Badge color="danger">
                                {t(selectedTransaction.status)}
                              </Badge>
                            ) : (
                              <Badge color="secondary">
                                {selectedTransaction.status}
                              </Badge>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td className="fw-bold">{t("Provider")}</td>
                          <td>{selectedTransaction.provider}</td>
                        </tr>
                        <tr>
                          <td className="fw-bold">{t("Service Name")}</td>
                          <td>{selectedTransaction.serviceName}</td>
                        </tr>
                        <tr>
                          <td className="fw-bold">{t("Service Identifier")}</td>
                          <td>{selectedTransaction.serviceIdentifier}</td>
                        </tr>
                        <tr>
                          <td className="fw-bold">{t("Terminal ID (System)")}</td>
                          <td>{selectedTransaction.terminalId}</td>
                        </tr>
                        <tr>
                          <td className="fw-bold">{t("Seller Found By")}</td>
                          <td>{selectedTransaction.sellerFoundBy}</td>
                        </tr>
                        <tr>
                          <td className="fw-bold">{t("Seller Transaction ID")}</td>
                          <td>{selectedTransaction.sellerTransactionId}</td>
                        </tr>
                        <tr>
                          <td className="fw-bold">{t("Created At")}</td>
                          <td>
                            {dayjs(selectedTransaction.createdAt).format(
                              "DD.MM.YYYY, HH:mm:ss"
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td className="fw-bold">{t("Updated At")}</td>
                          <td>
                            {dayjs(selectedTransaction.updatedAt).format(
                              "DD.MM.YYYY, HH:mm:ss"
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td className="fw-bold">{t("Payment Date")}</td>
                          <td>
                            {dayjs(selectedTransaction.paymentDate).format(
                              "DD.MM.YYYY, HH:mm:ss"
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </AccordionBody>
              </AccordionItem>

              {selectedTransaction.eraSaleCallbackPayment && (
                <AccordionItem>
                  <AccordionHeader targetId="2">{t("ERA Sale Callback Payment")}</AccordionHeader>
                  <AccordionBody accordionId="2">
                    <div className="table-responsive">
                      <table className="table table-bordered mb-0 text-black">
                        <tbody>
                          <tr>
                            <td className="fw-bold">{t("Payment ID")}</td>
                            <td>{selectedTransaction.eraSaleCallbackPayment.paymentId}</td>
                          </tr>
                          <tr>
                            <td className="fw-bold">{t("Seller Payment Task ID")}</td>
                            <td>
                              {
                                selectedTransaction.eraSaleCallbackPayment
                                  .sellerPaymentTaskId
                              }
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-bold">{t("ERA Sale Callback ID")}</td>
                            <td>
                              {
                                selectedTransaction.eraSaleCallbackPayment
                                  .eraSaleCallbackId
                              }
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-bold">{t("Payment Amount")}</td>
                            <td>
                              {(
                                selectedTransaction.eraSaleCallbackPayment.paymentAmount /
                                100
                              ).toLocaleString()}{" "}
                              uzs
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-bold">{t("Payment Transaction Value")}</td>
                            <td>
                              {(
                                selectedTransaction.eraSaleCallbackPayment
                                  .paymentTransactionValue / 100
                              ).toLocaleString()}{" "}
                              uzs
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-bold">{t("Payment Type")}</td>
                            <td>
                              {selectedTransaction.eraSaleCallbackPayment.paymentType}
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-bold">{t("POS Serial Number")}</td>
                            <td>
                              {
                                selectedTransaction.eraSaleCallbackPayment
                                  .posHardwareSerialNumber
                              }
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-bold">{t("Transaction Number")}</td>
                            <td>
                              {
                                selectedTransaction.eraSaleCallbackPayment
                                  .transactionNumber
                              }
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-bold">{t("Service Identifier")}</td>
                            <td>
                              {
                                selectedTransaction.eraSaleCallbackPayment
                                  .serviceIdentifier
                              }
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-bold">{t("Date")}</td>
                            <td>
                              {dayjs(
                                selectedTransaction.eraSaleCallbackPayment.date
                              ).format("DD.MM.YYYY, HH:mm:ss")}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </AccordionBody>
                </AccordionItem>
              )}

              {selectedTransaction.eraSaleCallbackPayment?.eraSaleCallback && (
                <AccordionItem>
                  <AccordionHeader targetId="3">{t("ERA Sale Callback (Inner)")}</AccordionHeader>
                  <AccordionBody accordionId="3">
                    <div className="table-responsive">
                      <table className="table table-bordered mb-0 text-black">
                        <tbody>
                          <tr>
                            <td className="fw-bold">{t("ID")}</td>
                            <td>
                              {
                                selectedTransaction.eraSaleCallbackPayment
                                  .eraSaleCallback.id
                              }
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-bold">{t("Is Refund")}</td>
                            <td>
                              {selectedTransaction.eraSaleCallbackPayment.eraSaleCallback
                                .isRefund
                                ? t("Yes")
                                : t("No")}
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-bold">{t("Service Identifier")}</td>
                            <td>
                              {
                                selectedTransaction.eraSaleCallbackPayment
                                  .eraSaleCallback.serviceIdentifier
                              }
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-bold">{t("Created At")}</td>
                            <td>
                              {dayjs(
                                selectedTransaction.eraSaleCallbackPayment
                                  .eraSaleCallback.createdAt
                              ).format("DD.MM.YYYY, HH:mm:ss")}
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-bold">{t("Updated At")}</td>
                            <td>
                              {dayjs(
                                selectedTransaction.eraSaleCallbackPayment
                                  .eraSaleCallback.updatedAt
                              ).format("DD.MM.YYYY, HH:mm:ss")}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </AccordionBody>
                </AccordionItem>
              )}

              {selectedTransaction.eraSaleCallbackPayment?.paymentInfoDto && (
                <AccordionItem>
                  <AccordionHeader targetId="4">{t("Payment Information DTO")}</AccordionHeader>
                  <AccordionBody accordionId="4">
                    <div className="table-responsive">
                      <table className="table table-bordered mb-0 text-black">
                        <tbody>
                          <tr>
                            <td className="fw-bold">{t("Payment System")}</td>
                            <td>
                              {
                                selectedTransaction.eraSaleCallbackPayment.paymentInfoDto
                                  .paymentSystemName
                              }
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-bold">{t("RRN")}</td>
                            <td>
                              {
                                selectedTransaction.eraSaleCallbackPayment.paymentInfoDto
                                  .rrn
                              }
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-bold">{t("Auth Code")}</td>
                            <td>
                              {
                                selectedTransaction.eraSaleCallbackPayment.paymentInfoDto
                                  .authCode
                              }
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-bold">{t("PAN")}</td>
                            <td>
                              {
                                selectedTransaction.eraSaleCallbackPayment.paymentInfoDto
                                  .pan
                              }
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-bold">{t("Merchant ID")}</td>
                            <td>
                              {
                                selectedTransaction.eraSaleCallbackPayment.paymentInfoDto
                                  .merchantId
                              }
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-bold">{t("Terminal ID")}</td>
                            <td>
                              {
                                selectedTransaction.eraSaleCallbackPayment.paymentInfoDto
                                  .terminalId
                              }
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-bold">{t("Response Code")}</td>
                            <td>
                              {
                                selectedTransaction.eraSaleCallbackPayment.paymentInfoDto
                                  .responseCode
                              }
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-bold">{t("STAN")}</td>
                            <td>
                              {
                                selectedTransaction.eraSaleCallbackPayment.paymentInfoDto
                                  .stan
                              }
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-bold">{t("Transaction Number")}</td>
                            <td>
                              {
                                selectedTransaction.eraSaleCallbackPayment.paymentInfoDto
                                  .transactionNumber
                              }
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </AccordionBody>
                </AccordionItem>
              )}
            </Accordion>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle}>
            {t("Close")}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default Transactions;
